import { useCallback, useEffect, useRef, useState } from "react";
import { createWsTicket, getWsBaseUrl } from "../api/wsTicketApi";

// Hook to stream microphone audio as 16k PCM over WebSocket and receive transcripts
// Backend protocol (expected):
// - Client -> Server:
//   { type: 'start', language: 'fr-FR', sampleRate: 16000 }
//   <binary audio frames: Int16 PCM mono 16kHz>
//   { type: 'stop' }
// - Server -> Client:
//   { type: 'partial', text: '...', isFinal: false }
//   { type: 'final', text: '...', isFinal: true }
//   { type: 'error', message: '...' }
//   { type: 'ended' }

const TARGET_SAMPLE_RATE = 16000;

function getWebSocketUrl() {
  const explicit = process.env.REACT_APP_STT_WS_URL;
  if (explicit) return explicit;

  const base = process.env.REACT_APP_BASE_API_URL || "";
  if (!base) return "";
  try {
    const url = new URL(base);
    const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProtocol}//${url.host}/stt/stream`;
  } catch {
    return "";
  }
}

function buildSttSocketUrl(wsToken) {
  const explicit = getWebSocketUrl();
  if (!explicit) return "";
  try {
    const url = new URL(explicit);
    url.searchParams.set("ws_token", wsToken);
    return url.toString();
  } catch {
    return "";
  }
}

function floatTo16BitPCM(float32Array) {
  const output = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

function downsampleBuffer(buffer, inputSampleRate, outSampleRate = TARGET_SAMPLE_RATE) {
  if (outSampleRate === inputSampleRate) {
    return floatTo16BitPCM(buffer);
  }
  const sampleRateRatio = inputSampleRate / outSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    // Simple averaging for downsampling window
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = count > 0 ? accum / count : 0;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return floatTo16BitPCM(result);
}

export function useStreamingSTT(defaultEvaluationId) {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const inputSampleRateRef = useRef(null);
  const onFinalRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const userStoppedRef = useRef(false);
  const languageRef = useRef("en-US");
  const evaluationIdRef = useRef(defaultEvaluationId || null);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const releaseMediaResources = useCallback(() => {
    try {
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current.onaudioprocess = null;
      }
      if (sourceNodeRef.current) sourceNodeRef.current.disconnect();
      if (audioCtxRef.current) audioCtxRef.current.close();
    } catch {}

    try {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    } catch {}

    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }

    audioCtxRef.current = null;
    processorRef.current = null;
    sourceNodeRef.current = null;
    mediaStreamRef.current = null;
    inputSampleRateRef.current = null;
  }, []);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    clearReconnectTimer();

    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "stop" }));
      }
    } catch {}

    if (wsRef.current) {
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }

    releaseMediaResources();
    reconnectAttemptRef.current = 0;
    setIsRecording(false);
    setIsConnecting(false);
  }, [clearReconnectTimer, releaseMediaResources]);

  const start = useCallback(async ({ language = "en-US", evaluationId: evaluationIdOverride } = {}, onFinal) => { // Make sure to pass FR if the test is in French
    const resolvedEvaluationId = evaluationIdOverride || evaluationIdRef.current || defaultEvaluationId;
    if (!resolvedEvaluationId) {
      setError("Missing evaluation id");
      return;
    }

    setError(null);
    setInterimTranscript("");
    onFinalRef.current = onFinal;
    languageRef.current = language;
    evaluationIdRef.current = resolvedEvaluationId;
    userStoppedRef.current = false;
    reconnectAttemptRef.current = 0;
    clearReconnectTimer();
    setIsConnecting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      inputSampleRateRef.current = audioCtx.sampleRate;

      const source = audioCtx.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      source.connect(processor);
      processor.connect(audioCtx.destination);

      processor.onaudioprocess = (e) => {
        const activeWs = wsRef.current;
        if (!activeWs || activeWs.readyState !== WebSocket.OPEN) return;
        const input = e.inputBuffer.getChannelData(0);
        const pcm16 = downsampleBuffer(input, inputSampleRateRef.current, TARGET_SAMPLE_RATE);
        activeWs.send(pcm16.buffer);
      };

      const connectSocket = async () => {
        if (userStoppedRef.current) return;
        const MAX_RECONNECT_ATTEMPTS = 8;
        const BASE_DELAY_MS = 500;
        const MAX_DELAY_MS = 10000;

        const scheduleReconnect = () => {
          if (userStoppedRef.current) return;
          if (reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
            setError("STT connection lost. Please restart dictation.");
            setIsConnecting(false);
            setIsRecording(false);
            releaseMediaResources();
            return;
          }
          const delay = Math.min(BASE_DELAY_MS * (2 ** reconnectAttemptRef.current), MAX_DELAY_MS);
          reconnectAttemptRef.current += 1;
          clearReconnectTimer();
          reconnectTimerRef.current = setTimeout(() => {
            connectSocket();
          }, delay);
        };

        try {
          const wsBase = getWsBaseUrl();
          if (!wsBase && !process.env.REACT_APP_STT_WS_URL) {
            throw new Error("STT WebSocket URL not configured");
          }
          const ticket = await createWsTicket(evaluationIdRef.current, "stt");
          if (userStoppedRef.current) return;

          const wsUrl = buildSttSocketUrl(ticket.ws_token);
          if (!wsUrl) {
            throw new Error("STT WebSocket URL not configured");
          }

          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;

          ws.onopen = () => {
            reconnectAttemptRef.current = 0;
            ws.send(
              JSON.stringify({
                type: "start",
                language: languageRef.current,
                sampleRate: TARGET_SAMPLE_RATE,
              })
            );
            setIsRecording(true);
            setIsConnecting(false);
          };

          ws.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.type === "partial") {
                setInterimTranscript(data.text || "");
              } else if (data.type === "final") {
                setInterimTranscript("");
                if (onFinalRef.current && data.text) onFinalRef.current(data.text);
              } else if (data.type === "error") {
                setError(data.message || "STT error");
              } else if (data.type === "ended") {
                // The socket close handler is the single source of truth for reconnect decisions.
              }
            } catch {
              // Some servers may send binary/control frames; ignore silently
            }
          };

          ws.onerror = () => {
            if (wsRef.current && wsRef.current !== ws) return;
            if (userStoppedRef.current) return;
            setError("WebSocket error");
          };

          ws.onclose = () => {
            if (wsRef.current && wsRef.current !== ws) return;
            wsRef.current = null;
            if (userStoppedRef.current) {
              setIsConnecting(false);
              setIsRecording(false);
              return;
            }
            setIsConnecting(true);
            setIsRecording(false);
            scheduleReconnect();
          };
        } catch (err) {
          if (userStoppedRef.current) return;
          setError(err && err.message ? err.message : "WebSocket error");
          setIsConnecting(true);
          setIsRecording(false);
          scheduleReconnect();
        }
      };

      connectSocket();
    } catch (err) {
      setError(err && err.message ? err.message : "Microphone error");
      setIsConnecting(false);
      stop();
    }
  }, [clearReconnectTimer, defaultEvaluationId, releaseMediaResources, stop]);

  useEffect(() => {
    evaluationIdRef.current = defaultEvaluationId || null;
  }, [defaultEvaluationId]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  // Short-lived end-to-end probe used by the mic pre-flight page: opens the
  // microphone, issues a ticket, connects the WebSocket, streams for
  // durationMs, then closes. Resolves with { success, error? }.
  const testConnection = useCallback(
    async ({ durationMs = 3000, language = "en-US", evaluationId: evaluationIdOverride } = {}) => {
      const resolvedEvaluationId = evaluationIdOverride || evaluationIdRef.current || defaultEvaluationId;
      if (!resolvedEvaluationId) {
        return { success: false, error: "Missing evaluation id" };
      }

      let stream = null;
      let audioCtx = null;
      let source = null;
      let processor = null;
      let ws = null;

      const cleanup = () => {
        try { if (processor) { processor.disconnect(); processor.onaudioprocess = null; } } catch {}
        try { if (source) source.disconnect(); } catch {}
        try { if (audioCtx) audioCtx.close(); } catch {}
        try { if (stream) stream.getTracks().forEach((t) => t.stop()); } catch {}
        try { if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "stop" })); } catch {}
        try { if (ws) ws.close(); } catch {}
      };

      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const inputRate = audioCtx.sampleRate;
        source = audioCtx.createMediaStreamSource(stream);
        processor = audioCtx.createScriptProcessor(4096, 1, 1);
        source.connect(processor);
        processor.connect(audioCtx.destination);

        const wsBase = getWsBaseUrl();
        if (!wsBase && !process.env.REACT_APP_STT_WS_URL) {
          throw new Error("STT WebSocket URL not configured");
        }
        const ticket = await createWsTicket(resolvedEvaluationId, "stt");
        const wsUrl = buildSttSocketUrl(ticket.ws_token);
        if (!wsUrl) throw new Error("STT WebSocket URL not configured");

        ws = new WebSocket(wsUrl);

        const result = await new Promise((resolve) => {
          let resolved = false;
          const finish = (value) => {
            if (resolved) return;
            resolved = true;
            resolve(value);
          };
          const timeoutId = setTimeout(
            () => finish({ success: false, error: "STT connection timed out" }),
            Math.max(1000, durationMs + 3000)
          );

          ws.onopen = () => {
            ws.send(JSON.stringify({ type: "start", language, sampleRate: TARGET_SAMPLE_RATE }));
            processor.onaudioprocess = (e) => {
              if (!ws || ws.readyState !== WebSocket.OPEN) return;
              const input = e.inputBuffer.getChannelData(0);
              const pcm16 = downsampleBuffer(input, inputRate, TARGET_SAMPLE_RATE);
              ws.send(pcm16.buffer);
            };
            setTimeout(() => {
              clearTimeout(timeoutId);
              finish({ success: true });
            }, durationMs);
          };
          ws.onerror = () => {
            clearTimeout(timeoutId);
            finish({ success: false, error: "WebSocket error" });
          };
          ws.onclose = (event) => {
            if (event && event.code === 1008) {
              clearTimeout(timeoutId);
              finish({ success: false, error: "STT handshake rejected" });
            }
          };
        });

        cleanup();
        return result;
      } catch (err) {
        cleanup();
        return { success: false, error: err && err.message ? err.message : "Microphone error" };
      }
    },
    [defaultEvaluationId]
  );

  return {
    isRecording,
    isConnecting,
    interimTranscript,
    error,
    start,
    stop,
    testConnection,
  };
}

export default useStreamingSTT;


